import { ErrorText } from './ErrorText';
import { Label } from './Label';

import { TextField, TextFieldProps } from '@release/ui/TextField';
import clsx from 'clsx';
import { ForwardedRef, forwardRef, ReactElement, ReactNode } from 'react';
import { Control, FieldPath, FieldValues, RegisterOptions, useController } from 'react-hook-form';

export interface RhfTextFieldProps<
  T extends FieldValues,
  TContext = object,
  TTransformedValues extends FieldValues = T,
> extends TextFieldProps {
  control: Control<T, TContext, TTransformedValues>;
  name: FieldPath<T>;
  label?: ReactNode;
  /** 入力欄の直前に差し込む操作 UI（例: AI 補完の提案）。文字列の補足説明は helperText を使う */
  suggestion?: ReactElement | boolean | null;
  required?: boolean;
  helperText?: ReactNode;
  direction?: 'row' | 'column';
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onTrailingIconClick?: () => void;
  rules?: RegisterOptions<T, FieldPath<T>>;
  'data-testid'?: string;
}

const RhfTextFieldWithRef = <T extends FieldValues, TContext = object, TTransformedValues extends FieldValues = T>(
  {
    control,
    name,
    label,
    suggestion,
    required,
    helperText,
    direction = 'column',
    onChange,
    onTrailingIconClick,
    rules,
    'data-testid': dataTestId,
    ...props
  }: RhfTextFieldProps<T, TContext, TTransformedValues>,
  ref: ForwardedRef<HTMLInputElement>
) => {
  const { field, fieldState } = useController({ control, name, rules });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      onChange(e);
    } else {
      field.onChange(e);
    }
  };

  // react-hook-form 用の ref と forwarded ref を結合
  const handleRef = (instance: HTMLInputElement | null) => {
    field.ref(instance);
    if (ref) {
      if (typeof ref === 'function') {
        ref(instance);
      } else {
        ref.current = instance;
      }
    }
  };

  const isError = Boolean(fieldState.error);

  return (
    <div className={clsx('flex w-full flex-col gap-y-1')} data-testid={dataTestId}>
      <div className={clsx('flex w-full', direction === 'column' ? 'flex-col gap-y-1' : 'items-center gap-x-2')}>
        {label && (
          <div className={clsx(direction === 'column' ? '' : 'min-w-[240px]')}>
            <Label label={label} required={required} helperText={helperText} />
          </div>
        )}
        {suggestion}
        <div className="flex w-full flex-col gap-y-1">
          <TextField
            {...field}
            {...props}
            fill
            isError={isError}
            onChange={handleChange}
            onTrailingIconClick={onTrailingIconClick}
            ref={handleRef}
          />
          <ErrorText error={fieldState.error} />
        </div>
      </div>
    </div>
  );
};

export const RhfTextField = forwardRef(RhfTextFieldWithRef) as <
  T extends FieldValues,
  TContext = object,
  TTransformedValues extends FieldValues = T,
>(
  props: RhfTextFieldProps<T, TContext, TTransformedValues> & { ref?: React.Ref<HTMLInputElement> }
) => React.ReactElement;
